import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { OnChange } from 'react-final-form-listeners'
import { Form } from 'react-final-form'
import TextInput from '../../input/textInput'
import QuestionHeader from './questionHeader'
import { Table, TableRow, TableCell, TableBody, Typography, makeStyles, Button, IconButton, Grid } from '@material-ui/core'
import ColumnResizer from 'column-resizer'
import { Delete } from '@material-ui/icons'
import { arrayToMap, deleteArrayIndex } from '../../../utils/utils'
import { useSession } from '../../session/session'
import DialogOptions from '../../dialog/dialogOptions'
import * as csv from 'papaparse'
import { download, tableToCSV } from 'utils/download'
import { useClient } from 'components/session/clientSession'
import { virtualtablecss } from 'utils/virtualtable'
import { Table as FluidTable } from 'react-fluid-table'
import { localize } from 'components/localization/strings'

const parsePromise = file => new Promise((complete, error) => { csv.parse(file, { complete, error }) })

const useStyles = makeStyles(() => ({
  table: {
    tableLayout: 'fixed',
    '& td': {
      border: '1px solid #eee',
      textAlign: 'center',
      padding: '5px'
    },
    '& tr:hover': {
      backgroundColor: '#fcfcfc'
    }
  },
  virtualtable: virtualtablecss,
  header: {
    fontWeight: '800',
  },
  cellHeader: {
    backgroundColor: '#eee',
  },
  cellPrompt: {
    backgroundColor: '#f9f9f9',
    overflow: 'hidden',
  },
  button: {
    backgroundColor: 'var(--button-color)',
    color: 'var(--button-text-color)',
    '&:hover': {
      backgroundColor: 'var(--button-color)',
    }
  },
  flex: {
    display: 'flex',
    marginTop: '5px'
  },
  grow: {
    flexGrow: 1,
  },
}))


export default function RenderTable({ elmIndex, elm, valIndex, valueChange, allowModify, preview, fullWidthButtons }) {
  const session = useSession()
  const clientSession = useClient()
  const classes = useStyles()
  if (!elm.Value) elm.Value = {}
  if (!elm.Value[valIndex]) elm.Value[valIndex] = new Array()

  const [initialValues, setInitialValues] = useState(null)
  const [tableMerged, setTableMerged] = useState(null)
  const [tableSize, setTableSize] = useState({})
  const [dialogOpen, setDialogOpen] = useState(false)
  const tableResizer = useRef()
  const localElm = useRef()
  const virtualref = useRef()

  const tableID = `_elm_holder_${elmIndex}_table_${location.pathname.split('/').pop()}`
  const namePrefix = `elm-${elmIndex}-val-${valIndex}`

  function elmValueChange(newVal, ri, ci) {
    if (ri >= (localElm.current.Value[valIndex].length ?? 0)) {
      for (let rowIndex = 0; rowIndex <= tableSize.rows; rowIndex++) {
        if (!localElm.current.Value[valIndex][rowIndex]) localElm.current.Value[valIndex][rowIndex] = {}
        for (let colIndex = 0; colIndex <= tableSize.cols; colIndex++) {
          if (!localElm.current.Value[valIndex][rowIndex][colIndex]) localElm.current.Value[valIndex][rowIndex][colIndex] = { Value: '' }
        }
      }
    }
    if (!localElm.current.Value[valIndex][ri]) localElm.current.Value[valIndex][ri] = {}
    if (!localElm.current.Value[valIndex][ri][ci]) localElm.current.Value[valIndex][ri][ci] = { Value: '' }
    localElm.current.Value[valIndex][ri][ci].Value = newVal
    valueChange(elmIndex, valIndex, localElm.current.Value[valIndex])
  }


  // Components ----------------------------------------------------------------------

  function StandardTable({ table, cols, rows, originalRows, allowBlanks }) {
    const colgroup = []
    for (let i = 0; i <= cols; i++) colgroup.push(<col key={i} />)
    if (rows > originalRows) colgroup.push(<col key='delete' style={{ width: '44px' }} />)

    setTimeout(() => {
      sessionStorage.removeItem(tableID) // Remove old table resize values to prevent column corruption
      const h = document.getElementById(`_elm_holder_${elmIndex}`)
      if (!h) return
      const t = h.querySelector('table')
      if (!t) return
      t.id = tableID
      tableResizer.current = new ColumnResizer(t, {
        gripInnerHtml: '<div></div>',
        minWidth: 8
      })
    }, 20)

    return <Table className={classes.table}>
      <colgroup>{colgroup}</colgroup>
      <TableBody>
        {table.map((row, ri) =>
          <TableRow key={ri}>
            {row.map((col, ci) => {
              let className = ''
              if (table[ri][ci].Prompt) {
                if (ri === 0 && localElm.current.Header) className = classes.cellHeader
                else className = classes.cellPrompt
              }
              return <TableCell key={ci} className={className}>
                <Cell col={col} ri={ri} ci={ci} valuechange={elmValueChange} allowBlanks={allowBlanks} />
              </TableCell>
            })}
            {ri > originalRows && <TableCell><DeleteCell ri={ri} /></TableCell>}
          </TableRow>
        )}
      </TableBody>
    </Table>
  }
  StandardTable.propTypes = {
    table: PropTypes.array.isRequired,
    cols: PropTypes.number.isRequired,
    rows: PropTypes.number.isRequired,
    originalRows: PropTypes.number.isRequired,
    allowBlanks: PropTypes.bool.isRequired
  }

  function VirtualTable({ table, rows, originalRows, allowBlanks }) {
    const skipRow = elm.Header || table[0].every(e => e.Prompt) ? 0 : -1 // if the first row is a header or if it is all prompts, use the first row as column headers
    const data = table.map((row, ri) => {
      const out = {}
      row.forEach((col, ci) => out[ci] = ri > skipRow ? <Cell col={col} ri={ri} ci={ci} valuechange={elmValueChange} allowBlanks={allowBlanks} /> : '')
      if (ri > originalRows) out['del'] = <DeleteCell ri={ri} />
      return out
    })
    const columns = table[0].map((col, ci) => {
      return {
        key: ci,
        header: skipRow > -1 ? col.Prompt : ''
      }
    })
    if (rows > originalRows) {
      columns.push({
        key: 'del',
        header: '',
        width: 50
      })
    }
    return <FluidTable data={data} columns={columns} tableHeight={500} className={classes.virtualtable} ref={virtualref} />
  }
  VirtualTable.propTypes = {
    table: PropTypes.array.isRequired,
    rows: PropTypes.number.isRequired,
    originalRows: PropTypes.number.isRequired,
    allowBlanks: PropTypes.bool.isRequired
  }

  function InputBox({ name, valuechange, validationrule, required, ri, ci }) {
    return <div style={{ width: '100%' }}>
      <TextInput valuechange={valuechange} disabled={!allowModify} name={name} fullWidth={true} required={required} variant="outlined" size="small" validationrule={validationrule} />
      <OnChange name={name}>
        {newVal => valuechange(newVal, ri, ci)}
      </OnChange>
    </div>
  }
  InputBox.propTypes = {
    ri: PropTypes.number.isRequired,
    ci: PropTypes.number.isRequired,
    valuechange: PropTypes.func.isRequired,
    validationrule: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
  }

  function PromptBox({ header, children }) {
    return <Typography className={header ? classes.header : ''} style={{ width: '100%' }}>{children}</Typography>
  }
  PromptBox.propTypes = {
    children: PropTypes.node.isRequired,
    header: PropTypes.bool,
  }

  function Cell({ col, ri, ci, valuechange, allowBlanks }) {
    if (col.Prompt) return <PromptBox header={ri === 0 && elm.Header}>{
      (col.Prompt.startsWith('https://') || col.Prompt.startsWith('http://')) ?
        <a href={col.Prompt} target='_blank' rel='noreferrer'>{col.Prompt}</a> :
        col.Prompt
    }
    </PromptBox>
    return <InputBox
      ri={ri} ci={ci}
      name={`${namePrefix}-${ri}-${ci}`}
      validationrule={col.ValidationRule || 'Any'}
      required={!(allowBlanks || col.AllowBlanks)}
      valuechange={valuechange}
    />
  }
  Cell.propTypes = {
    col: PropTypes.object.isRequired,
    ri: PropTypes.number.isRequired,
    ci: PropTypes.number.isRequired,
    valuechange: PropTypes.func.isRequired,
    allowBlanks: PropTypes.bool.isRequired
  }

  function DeleteCell({ ri }) {
    function deleteCell() {
      tableResizer?.current?.reset({ disable: true })
      setTimeout(() => virtualref?.current?.scrollToItem(ri + 1), 10)
      localElm.current.Value[valIndex] = arrayToMap(deleteArrayIndex(Object.values(localElm.current.Value[valIndex]), ri))
      valueChange(elmIndex, valIndex, localElm.current.Value[valIndex])
      setInitialValues(null)
    }
    return <IconButton size='small' className={classes.button} aria-label='delete' onClick={deleteCell}>
      <Delete fontSize='small' />
    </IconButton>
  }
  DeleteCell.propTypes = {
    ri: PropTypes.number.isRequired,
  }

  // End Components ---------------------------------------------------------------------

  // Actions ----------------------------

  function addRow() {
    tableResizer?.current?.reset({ disable: true })
    const newRow = [[]]
    for (let i = 0; i <= tableSize.cols; i++) newRow[0].push({ Value: '', AllowBlanks: true })
    setTableMerged(tableMerged.concat(newRow))
    setTableSize({
      cols: tableSize.cols,
      rows: tableSize.rows + 1,
      originalRows: tableSize.originalRows
    })
    setTimeout(() => virtualref?.current?.scrollToItem(tableSize.rows + 2), 10)
  }

  function fillWithCSV() {
    tableResizer?.current?.reset({ disable: true })
    const f = document.createElement('input')
    f.style.display = 'none'
    f.type = 'file'
    f.accept = '.csv'
    f.id = 'bulkFileUpload'
    document.body.appendChild(f)
    f.addEventListener('change', async e => {
      for (let file of [...e.target.files]) {
        if (file.name.endsWith('.csv')) {
          const results = await parsePromise(file)
          results.data.forEach((dataRow, rowIndex) => {
            if (!localElm.current.AddRow && rowIndex > tableSize.rows) return
            if (!localElm.current.Value[valIndex][rowIndex]) localElm.current.Value[valIndex][rowIndex] = {}
            for (let i = 0; i <= tableSize.cols; i++) {
              localElm.current.Value[valIndex][rowIndex][i] = {}
              if (rowIndex > tableSize.rows) localElm.current.Value[valIndex][rowIndex][i].AllowBlanks = true
            }
            dataRow.forEach((val, colIndex) => {
              if (colIndex > tableSize.cols) return
              localElm.current.Value[valIndex][rowIndex][colIndex].Value = val
            })
          })
        }
      }
      valueChange(elmIndex, valIndex, localElm.current.Value[valIndex])

      setInitialValues({}) // I dont understand why this is extra state setting necessary, but without it uploading a CSV twice doesn't work. Maybe it forces a second re-render or something?

      setInitialValues(null)
      setDialogOpen(false)
      document.body.removeChild(f)
    })
    f.dispatchEvent(new MouseEvent('click'))
  }

  // End Actions ------------------------

  // Create Initial Values and Merged Table
  if (!initialValues || !localElm.current || !elm.__isLocal) {

    if (!localElm.current || !elm.__isLocal || preview) {
      elm.__isLocal = true
      localElm.current = JSON.parse(JSON.stringify(elm)) // Create copy of current element so we can force a rerender on remote changes
    }

    const fullRows = JSON.parse(JSON.stringify(localElm.current?.Table ?? {}))
    const localInitialValues = {}
    let originalRows = Object.values(fullRows).length - 1
    let numRows = 0
    let numCols = 0
    const t = Object.values(localElm.current.Value[valIndex] || {}).length > Object.values(localElm.current?.Table ?? {}).length ? localElm.current.Value[valIndex] : localElm.current?.Table ?? {}
    const localTableMerged = Object.values(t).map((row, ri) => {
      if (ri > numRows) numRows = ri
      if (!fullRows[ri]) fullRows[ri] = {}
      return Object.values(row).map((col, ci) => {
        if (ci > numCols) numCols = ci
        // Map values into the full rows copy, and if there are added rows, add those as well
        if (!fullRows[ri][ci]) fullRows[ri][ci] = col // If there are more values than rows, we can safely assume "col" comes from the values and we can add it to the full table
        else if (localElm.current.Value?.[valIndex]?.[ri]?.[ci]) fullRows[ri][ci] = { ...fullRows[ri][ci], ...localElm.current.Value[valIndex][ri][ci] } // else values if they exist into the table
        // Flatten into initial values to pass to Final Form
        localInitialValues[`${namePrefix}-${ri}-${ci}`] = fullRows[ri][ci].Value || ''
        return fullRows[ri][ci]
      })
    })
    setInitialValues(localInitialValues)
    setTableMerged(localTableMerged)
    setTableSize({
      rows: numRows,
      cols: numCols,
      originalRows: originalRows
    })
  }

  return <div key={elmIndex}>
    <QuestionHeader helpTooltip={elm.ShowHelpTooltip ? elm.HelpTooltip : ''} prompt={localElm.current.Prompt} allowBlanks={!!localElm.current.AllowBlanks} />
    <Form onSubmit={() => { }}
      subscription={{}}
      initialValues={initialValues}
      //render={({ values }) => (<>
      render={() => (<>
        {tableMerged.length > 30
          ? <VirtualTable table={tableMerged} rows={tableSize.rows} originalRows={tableSize.originalRows} allowBlanks={!!localElm.current.AllowBlanks} />
          : <StandardTable table={tableMerged} cols={tableSize.cols} rows={tableSize.rows} originalRows={tableSize.originalRows} allowBlanks={!!localElm.current.AllowBlanks} />
        }
        <div className={classes.flex}>
          <div className={classes.grow} />
          <Grid container spacing={1} style={fullWidthButtons ? {} : { width: 'initial' }}>
            {((allowModify) || !(session.external || clientSession.forceClientView)) &&
              <>
                {localElm.current.AddRow && <Grid item xs={fullWidthButtons ? 12 : false}><Button fullWidth={fullWidthButtons || false} variant="contained" className={classes.button} onClick={addRow}>{localize('Add', clientSession?.client?.Language)} {typeof localElm.current.AddRow === 'string' ? localElm.current.AddRow : localize('Row', clientSession?.client?.Language)}</Button></Grid>}
                {localElm.current.CSVUpload &&
                  <Grid item xs={fullWidthButtons ? 12 : false}>
                    <Button fullWidth={fullWidthButtons || false} variant="contained" className={classes.button} onClick={() => {
                      if (localElm.current.CSVPrompt) setDialogOpen(true)
                      else fillWithCSV()
                    }}>{localize('Upload CSV to Fill Table', clientSession?.client?.Language)}</Button>
                    <DialogOptions open={dialogOpen} setOpen={setDialogOpen} title='CSV Upload' content={localElm.current.CSVPrompt || ''} options={[{
                      content: 'Upload',
                      action: () => fillWithCSV()
                    }]} />
                  </Grid>
                }
              </>
            }
            {localElm.current.CSVDownload &&
              <Grid item xs={fullWidthButtons ? 12 : false}>
                <Button fullWidth={fullWidthButtons || false} variant="contained" className={classes.button} onClick={() => {
                  download(tableToCSV(localElm.current, valIndex), localElm.current.Prompt + ' table', 'CSV')
                }}>{localize('Download Table as CSV', clientSession?.client?.Language)}</Button>
              </Grid>
            }
          </Grid>
        </div>
      </>)
      } //debug: <pre>{JSON.stringify(values, 0, 2)}</pre>
    />
  </div>
}

RenderTable.propTypes = {
  elmIndex: PropTypes.number.isRequired,
  elm: PropTypes.object.isRequired,
  valIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  valueChange: PropTypes.func.isRequired,
  allowModify: PropTypes.bool.isRequired,
  preview: PropTypes.bool,
  fullWidthButtons: PropTypes.bool
}